import React from 'react';
import { format } from 'date-fns';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Skeleton } from '@/shared/ui/skeleton';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Lock } from 'lucide-react';
import { RecordActionsMenu } from '@/shared/components/RecordActionsMenu';
import type {
  PunchListPriority,
  PunchListStatus,
  PunchListItemSummary,
  PunchListsSortKey,
  PunchListsSortState,
  LocationMinimal,
} from '../types';
import { PRIORITY_LABELS, STATUS_LABELS, formatUnitLabel } from '../types';

// Solid, filled status pills (mirrors the dense "In Progress" pill style).
const STATUS_BADGE: Record<PunchListStatus, string> = {
  OPEN: 'border-transparent bg-blue-600 text-white hover:bg-blue-600',
  PENDING_APPROVAL: 'border-transparent bg-amber-500 text-white hover:bg-amber-500',
  COMPLETED: 'border-transparent bg-green-600 text-white hover:bg-green-600',
  OVERDUE: 'border-transparent bg-red-600 text-white hover:bg-red-600',
  CLOSED: 'border-transparent bg-slate-600 text-white hover:bg-slate-600',
};

// Lighter tinted priority pills so status stays the dominant signal.
const PRIORITY_BADGE: Record<PunchListPriority, string> = {
  LOW: 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  MEDIUM: 'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
  HIGH: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return format(new Date(iso), 'MMM d, yyyy');
};

const COLUMN_COUNT = 11;

interface Props {
  items: PunchListItemSummary[];
  isLoading: boolean;
  error?: unknown;
  onRowClick: (item: PunchListItemSummary) => void;
  onEdit: (item: PunchListItemSummary) => void;
  onDelete: (item: PunchListItemSummary) => void;
  sort: readonly PunchListsSortState[];
  onSortChange: (key: PunchListsSortKey) => void;
  selectedItemIds: Set<string>;
  allSelectableSelected: boolean;
  partiallySelected: boolean;
  onSelectItem: (itemId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  selectAllDisabled?: boolean;
}

export const PunchListsTable: React.FC<Props> = ({
  items,
  isLoading,
  error,
  onRowClick,
  onEdit,
  onDelete,
  sort,
  onSortChange,
  selectedItemIds,
  allSelectableSelected,
  partiallySelected,
  onSelectItem,
  onSelectAll,
  selectAllDisabled = false,
}) => {
  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
          <TableCell key={j} className="py-2">
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ));

  return (
    <div className="min-w-0 space-y-2" data-tour-id="punch-lists-table">
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-64 w-full rounded-md" />
          ))
        ) : error ? (
          <div className="rounded-md border px-4 py-8 text-center text-sm text-destructive">
            Failed to load punch list items.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border px-4 py-10 text-center text-sm text-muted-foreground">
            No punch list items found.
          </div>
        ) : (
          items.map((item) => {
            const selected = selectedItemIds.has(item.id);

            return (
              <div
                key={item.id}
                className="w-full min-w-0 rounded-md border bg-card p-4 transition-colors hover:bg-muted/50 data-[state=selected]:border-primary data-[state=selected]:bg-primary/5"
                data-state={selected ? 'selected' : undefined}
              >
                <div className="flex min-w-0 items-start gap-3">
                  <Checkbox
                    aria-label={`Select ${item.title}`}
                    checked={selected}
                    className="mt-0.5 shrink-0"
                    onCheckedChange={(checked) => onSelectItem(item.id, checked === true)}
                  />
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => onRowClick(item)}
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-start gap-1.5">
                          {item.is_private && (
                            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          )}
                          <p className="min-w-0 break-words text-sm font-semibold">{item.title}</p>
                        </div>
                      </div>
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>

                    <div className="mt-4 grid min-w-0 grid-cols-2 gap-x-4 gap-y-3">
                      <MobileCardField label="Status">
                        <Badge
                          className={`max-w-full whitespace-normal text-left text-xs ${STATUS_BADGE[item.status]}`}
                        >
                          {STATUS_LABELS[item.status]}
                        </Badge>
                      </MobileCardField>
                      <MobileCardField label="Priority">
                        <Badge
                          className={`max-w-full whitespace-normal text-left text-xs ${PRIORITY_BADGE[item.priority]}`}
                        >
                          {PRIORITY_LABELS[item.priority]}
                        </Badge>
                      </MobileCardField>
                      <MobileCardField label="Assignee">
                        {item.assignee
                          ? `${item.assignee.first_name} ${item.assignee.last_name}`
                          : 'Unassigned'}
                      </MobileCardField>
                      <MobileCardField label="Due Date">
                        {formatDate(item.due_date)}
                      </MobileCardField>
                      <MobileCardField label="Type">{item.item_type?.name ?? '—'}</MobileCardField>
                      <MobileCardField label="Trade">{item.trade?.name ?? '—'}</MobileCardField>
                      <MobileCardField label="Location">
                        <LocationValue location={item.location} />
                      </MobileCardField>
                      <MobileCardField label="Created">
                        {formatDate(item.created_at)}
                      </MobileCardField>
                    </div>
                  </button>
                  <RecordActionsMenu
                    label={`punch item ${item.title}`}
                    canEdit={item.can_edit}
                    canDelete={item.can_delete}
                    onView={() => onRowClick(item)}
                    onEdit={() => onEdit(item)}
                    onDelete={() => onDelete(item)}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 w-[60px] bg-background text-xs font-medium">
                <Checkbox
                  aria-label="Select all punch list items"
                  checked={
                    allSelectableSelected ? true : partiallySelected ? 'indeterminate' : false
                  }
                  disabled={selectAllDisabled || items.length === 0}
                  onCheckedChange={(checked) => onSelectAll(checked === true)}
                  title="Select all filtered punch list items"
                />
              </TableHead>
              <TableHead className="text-xs font-medium">Title</TableHead>
              <SortableHead
                className="w-[120px]"
                label="Status"
                sortKey="status"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead
                className="w-[110px]"
                label="Priority"
                sortKey="priority"
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableHead className="text-xs font-medium">Assignee</TableHead>
              <TableHead className="text-xs font-medium">Trade</TableHead>
              <SortableHead
                label="Type"
                sortKey="item_type"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead
                label="Location"
                sortKey="location"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead
                className="whitespace-nowrap"
                label="Due Date"
                sortKey="due_date"
                sort={sort}
                onSortChange={onSortChange}
              />
              <SortableHead
                className="whitespace-nowrap"
                label="Created"
                sortKey="created_at"
                sort={sort}
                onSortChange={onSortChange}
              />
              <TableHead className="w-[72px] text-xs font-medium">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              renderSkeletonRows()
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-8 text-center text-destructive">
                  Failed to load punch list items.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  className="py-12 text-center text-sm text-muted-foreground"
                >
                  No punch list items found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <PunchListRow
                  key={item.id}
                  item={item}
                  onView={onRowClick}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  selected={selectedItemIds.has(item.id)}
                  onSelectItem={onSelectItem}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

function MobileCardField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 min-w-0 break-words text-xs font-medium text-foreground">{children}</div>
    </div>
  );
}

function SortableHead({
  label,
  sortKey,
  sort,
  onSortChange,
  className,
}: {
  label: string;
  sortKey: PunchListsSortKey;
  sort: readonly PunchListsSortState[];
  onSortChange: (key: PunchListsSortKey) => void;
  className?: string;
}) {
  const sortIndex = sort.findIndex((rule) => rule.key === sortKey);
  const activeRule = sortIndex >= 0 ? sort[sortIndex] : null;
  const Icon = activeRule ? (activeRule.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <TableHead className={`text-xs font-medium ${className ?? ''}`}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onSortChange(sortKey)}
        className="-ml-2 h-8 gap-1 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
        title={`Sort by ${label}`}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
        {activeRule && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/15 px-1 text-[10px] font-bold text-primary">
            {sortIndex + 1}
          </span>
        )}
      </Button>
    </TableHead>
  );
}

function PunchListRow({
  item,
  onView,
  onEdit,
  onDelete,
  selected,
  onSelectItem,
}: {
  item: PunchListItemSummary;
  onView: (item: PunchListItemSummary) => void;
  onEdit: (item: PunchListItemSummary) => void;
  onDelete: (item: PunchListItemSummary) => void;
  selected: boolean;
  onSelectItem: (itemId: string, checked: boolean) => void;
}) {
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      data-state={selected ? 'selected' : undefined}
      onClick={() => onView(item)}
    >
      <TableCell className="sticky left-0 z-10 bg-background py-1.5">
        <Checkbox
          aria-label={`Select ${item.title}`}
          checked={selected}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onCheckedChange={(checked) => onSelectItem(item.id, checked === true)}
        />
      </TableCell>
      <TableCell className="max-w-[240px] truncate py-1.5 text-xs font-medium">
        <div className="flex items-center gap-1">
          {item.is_private && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />}
          <span className="truncate">{item.title}</span>
        </div>
      </TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${STATUS_BADGE[item.status]}`}>
          {STATUS_LABELS[item.status]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${PRIORITY_BADGE[item.priority]}`}>
          {PRIORITY_LABELS[item.priority]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.assignee ? `${item.assignee.first_name} ${item.assignee.last_name}` : '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.trade?.name ?? '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.item_type?.name ?? '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        <LocationValue location={item.location} />
      </TableCell>
      <TableCell className="py-1.5 text-xs whitespace-nowrap text-muted-foreground">
        {formatDate(item.due_date)}
      </TableCell>
      <TableCell className="py-1.5 text-xs whitespace-nowrap text-muted-foreground">
        {formatDate(item.created_at)}
      </TableCell>
      <TableCell className="py-1.5">
        <RecordActionsMenu
          label={`punch item ${item.title}`}
          canEdit={item.can_edit}
          canDelete={item.can_delete}
          onView={() => onView(item)}
          onEdit={() => onEdit(item)}
          onDelete={() => onDelete(item)}
        />
      </TableCell>
    </TableRow>
  );
}

function LocationValue({ location }: { location: LocationMinimal | null }) {
  if (!location) return <>—</>;
  const areaTagSets = location.area_tag_sets ?? [];
  const areaTagSetNames = areaTagSets.map((areaTagSet) => areaTagSet.name).join(', ');

  return (
    <div className="min-w-0">
      <div className="truncate text-foreground" title={formatUnitLabel(location)}>
        {formatUnitLabel(location)}
      </div>
      {areaTagSets.length > 0 && (
        <div
          className="mt-0.5 truncate text-[11px] font-medium text-primary"
          title={areaTagSetNames}
        >
          {areaTagSets.length === 1 ? 'Phase/Zone' : 'Phases/Zones'}: {areaTagSetNames}
        </div>
      )}
    </div>
  );
}

export default PunchListsTable;
